import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FRAME_COLOUR_SWATCH } from '../design/products';
import type { Camera } from '../design/schema';
import Poster, { type PosterHandle } from '../poster/Poster';
import { posterLayout } from '../poster/layout';
import { IconEye, IconFit, IconMinus, IconPlus, IconRedo, IconUndo, IconZoomIn, IconZoomOut } from './icons';
import { newLabel } from './panels';
import { useEditor } from './store';

/** How far the phone view can magnify the poster, for lining up labels. The print is unaffected. */
const VIEW_ZOOMS = [1, 1.5, 2, 3];
const SMALL_SCREEN = '(max-width: 860px)';

function useSmallScreen() {
  const [small, setSmall] = useState(() => matchMedia(SMALL_SCREEN).matches);
  useEffect(() => {
    const mq = matchMedia(SMALL_SCREEN);
    const on = () => setSmall(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return small;
}

interface StageProps {
  onZoom: (delta: number) => void;
  onFit: () => void;
  onPreview: () => void;
}

/** The poster, scaled to fit whatever space the screen gives it, with its floating tools. */
const Stage = forwardRef<PosterHandle, StageProps>(function Stage({ onZoom, onFit, onPreview }, posterRef) {
  const { state, edit, dispatch } = useEditor();
  const { design } = state;
  const layout = posterLayout(design.product);
  const areaRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const hasRoute = design.routes.length > 0;

  // View zoom (phones only): magnifies the poster on screen and lets it scroll. The map is held
  // still meanwhile, so a drag looks around rather than reframing the print.
  const smallScreen = useSmallScreen();
  const [viewStep, setViewStep] = useState(0);
  const viewZoom = smallScreen && hasRoute ? VIEW_ZOOMS[viewStep] : 1;
  const zoomed = viewZoom > 1;
  const shown = scale * viewZoom;
  // Where to centre the view after the next zoom, as a fraction of the scrollable content.
  const focusRef = useRef<{ x: number; y: number } | null>(null);

  const changeView = (step: number) => {
    const area = areaRef.current;
    const next = Math.max(0, Math.min(VIEW_ZOOMS.length - 1, step));
    if (!area || next === viewStep) return;
    const box = area.getBoundingClientRect();
    // Zooming in onto a selected label keeps it in view; otherwise keep the current centre.
    const label = state.selectedLabel && next > viewStep
      ? area.querySelector<HTMLElement>(`.tmp-label[data-id="${CSS.escape(state.selectedLabel)}"]`)
      : null;
    const r = label?.getBoundingClientRect();
    const cx = r ? r.left + r.width / 2 - box.left + area.scrollLeft : area.scrollLeft + area.clientWidth / 2;
    const cy = r ? r.top + r.height / 2 - box.top + area.scrollTop : area.scrollTop + area.clientHeight / 2;
    focusRef.current = { x: cx / area.scrollWidth, y: cy / area.scrollHeight };
    setViewStep(next);
  };

  useLayoutEffect(() => {
    const area = areaRef.current;
    const f = focusRef.current;
    focusRef.current = null;
    if (!area || !f) return;
    area.scrollLeft = f.x * area.scrollWidth - area.clientWidth / 2;
    area.scrollTop = f.y * area.scrollHeight - area.clientHeight / 2;
  }, [viewZoom]);

  // The chosen frame and mount are drawn around the poster on the stage, in screen pixels, so a
  // change in Size & framing shows at once. They are decoration only: the print is the poster.
  const framed = design.product.framing !== 'Unframed';
  const mounted = design.product.framing === 'Framed with white mount';
  const small = areaRef.current != null && (areaRef.current.clientWidth < 560 || areaRef.current.clientHeight < 420);
  const framePx = framed ? (small ? 8 : 12) : 0;
  const mountPx = mounted ? (small ? 16 : 28) : 0;
  const chrome = framePx + mountPx;
  const frameColour = framed && design.product.frameColour !== 'N/A' ? FRAME_COLOUR_SWATCH[design.product.frameColour] : '#141414';

  useLayoutEffect(() => {
    const area = areaRef.current!;
    const fit = () => {
      const pad = area.clientWidth < 560 ? 12 : 28;
      const s = Math.min((area.clientWidth - pad * 2 - chrome * 2) / layout.width, (area.clientHeight - pad * 2 - chrome * 2) / layout.height, 1);
      setScale(Math.max(0.12, s));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(area);
    return () => ro.disconnect();
  }, [layout.width, layout.height, chrome]);

  const setCamera = (camera: Camera) => edit((d) => ({ ...d, camera }), 'camera');

  return (
    <div className="tmd-stage">
      <div className={`tmd-stage-area${zoomed ? ' is-zoomed' : ''}`} ref={areaRef}>
        {/* The outer box takes the scaled size in the layout; the inner one is the full-size poster, transformed. */}
        {/* Mounted only once the scale is known: the map measures its container when it is created. */}
        {scale > 0 && (
        <div
          className={`tmd-poster-box${framed ? ' is-framed' : ''}${mounted ? ' has-mount' : ''}`}
          style={{ width: layout.width * shown + chrome * 2, height: layout.height * shown + chrome * 2, padding: framePx, background: frameColour }}
        >
          <div className="tmd-poster-mount" style={{ padding: mountPx }}>
          {/* Sized to the scaled poster: the transformed element below still occupies its full,
              unscaled size in the layout, which would otherwise stretch the mount. */}
          <div style={{ width: layout.width * shown, height: layout.height * shown }}>
          <div style={{ width: layout.width, height: layout.height, transform: `scale(${shown})`, transformOrigin: '0 0' }}>
            <Poster
              ref={posterRef}
              design={design}
              interactive
              locked={zoomed}
              placing={state.placing}
              selectedLabel={state.selectedLabel}
              onCamera={setCamera}
              onAddLabel={(lng, lat) => {
                const label = newLabel(lng, lat);
                edit((d) => ({ ...d, labels: [...d.labels, label] }));
                dispatch({ type: 'selectLabel', id: label.id });
              }}
              onSelectLabel={(id) => (id || state.selectedLabel) && dispatch({ type: 'selectLabel', id })}
              onMoveLabel={(id, lng, lat) => edit((d) => ({ ...d, labels: d.labels.map((l) => (l.id === id ? { ...l, lng, lat } : l)) }))}
            />
          </div>
          </div>
          </div>
        </div>
        )}
      </div>

      {hasRoute && (
        <div className="tmd-tools" role="toolbar" aria-label="Map tools">
          <button type="button" onClick={() => dispatch({ type: 'undo' })} disabled={!state.past.length} aria-label="Undo" title="Undo">
            <IconUndo />
          </button>
          <button type="button" onClick={() => dispatch({ type: 'redo' })} disabled={!state.future.length} aria-label="Redo" title="Redo">
            <IconRedo />
          </button>
          <i />
          <button type="button" onClick={() => onZoom(-0.25)} aria-label="Zoom out" title="Zoom out">
            <IconMinus />
          </button>
          <button type="button" onClick={() => onZoom(0.25)} aria-label="Zoom in" title="Zoom in">
            <IconPlus />
          </button>
          <button type="button" onClick={onFit} disabled={design.camera == null} aria-label="Fit whole route" title="Fit whole route">
            <IconFit />
          </button>
          <i />
          <button type="button" className="has-text" onClick={onPreview}>
            <IconEye /> <span>Preview</span>
          </button>
        </div>
      )}
      {hasRoute && smallScreen && (
        <div className="tmd-view-zoom" role="group" aria-label="Magnify the view">
          <button type="button" onClick={() => changeView(viewStep + 1)} disabled={viewStep === VIEW_ZOOMS.length - 1} aria-label="Magnify" title="Magnify">
            <IconZoomIn />
          </button>
          {zoomed && (
            <button type="button" className="is-level" onClick={() => changeView(0)} aria-label="Back to whole poster" title="Back to whole poster">
              {VIEW_ZOOMS[viewStep]}×
            </button>
          )}
          <button type="button" onClick={() => changeView(viewStep - 1)} disabled={!zoomed} aria-label="Reduce" title="Reduce">
            <IconZoomOut />
          </button>
        </div>
      )}
      {state.placing && <div className="tmd-stage-hint">Tap the map to place your label</div>}
    </div>
  );
});

export default Stage;
